import React, { useState, useCallback, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Tooltip,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BugReportIcon from "@mui/icons-material/BugReport";
import DescriptionIcon from "@mui/icons-material/Description";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import api from "../../services/api";
import consultaCepComIaTemplate from "./templates/consultaCepComIaTemplate";
import consultaProdutosComIaTemplate from "./templates/consultaProdutosComIaTemplate";

const ACCENT = "#8b5cf6";
const WIDTH = 380;

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    width: WIDTH,
    height: "100%",
    backgroundColor: "#fff",
    borderLeft: `2px solid ${ACCENT}20`,
    boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
    zIndex: 25,
    display: "flex",
    flexDirection: "column",
  },
  hidden: {
    transform: `translateX(${WIDTH + 10}px)`,
    pointerEvents: "none",
    visibility: "hidden",
    // Visibility muda APOS a animacao de saida terminar (0.3s delay)
    transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1), visibility 0s linear 0.3s",
  },
  visible: {
    transform: "translateX(0)",
    pointerEvents: "auto",
    visibility: "visible",
    // Visibility muda IMEDIATAMENTE ao abrir (0s delay)
    transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1), visibility 0s linear 0s",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid #eee",
    gap: 8,
  },
  headerIcon: {
    color: ACCENT,
    fontSize: 24,
  },
  headerTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  quickActions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  actionBtn: {
    textTransform: "none",
    justifyContent: "flex-start",
    padding: "10px 14px",
    borderRadius: 10,
    fontWeight: 500,
    fontSize: 13,
    border: "1px solid #e5e7eb",
    backgroundColor: "#fafafa",
    color: "#333",
    "&:hover": {
      backgroundColor: "#f3f0ff",
      borderColor: ACCENT,
    },
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    overflowY: "auto",
    paddingBottom: 8,
  },
  messageBubble: {
    padding: "10px 14px",
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.5,
    maxWidth: "100%",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userMsg: {
    backgroundColor: `${ACCENT}10`,
    border: `1px solid ${ACCENT}25`,
    color: "#333",
    alignSelf: "flex-end",
  },
  aiMsg: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e5e7eb",
    color: "#333",
    alignSelf: "flex-start",
  },
  inputArea: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    padding: "12px 16px",
    borderTop: "1px solid #eee",
    backgroundColor: "#fafafa",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 13,
      backgroundColor: "#fff",
    },
  },
  issueCard: {
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 12,
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 4,
    cursor: "pointer",
    transition: "transform 0.15s, box-shadow 0.15s",
    "&:hover": {
      transform: "translateX(2px)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  issueError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
  },
  issueWarning: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#92400e",
  },
  generateBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    padding: "10px 16px",
    backgroundColor: ACCENT,
    color: "#fff",
    "&:hover": {
      backgroundColor: "#7c3aed",
    },
  },
  tokenInfo: {
    fontSize: 10,
    color: "#9ca3af",
    textAlign: "right",
    marginTop: 4,
  },
}));

const QUICK_PROMPTS = [
  { label: "Atendimento financeiro", text: "Fluxo de atendimento para empresa financeira com menu: 2ª via de boleto, consultar saldo, falar com atendente" },
  { label: "Suporte técnico", text: "Fluxo de suporte técnico com triagem: problema de conexão, problema de software, problema de hardware, falar com técnico" },
  { label: "Agendamento", text: "Fluxo para agendar consulta/serviço: coletar nome, data preferida, horário, confirmar agendamento" },
  { label: "Pesquisa de satisfação", text: "Fluxo de pesquisa de satisfação: nota de 1 a 5, se nota baixa perguntar motivo, agradecer" },
  {
    label: "Consulta de CEP com IA",
    flowData: consultaCepComIaTemplate,
  },
  {
    label: "Consulta Produtos com IA",
    flowData: consultaProdutosComIaTemplate,
  },
];

export default function AICopilot({ open, onClose, nodes, edges, onApplyFlow, onHighlightNode }) {
  const classes = useStyles();
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  // Helper: resolve nodeId → nome legivel (label ou type)
  const getNodeLabel = useCallback((nodeId) => {
    if (!nodeId || !nodes?.length) return null;
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return null;
    return node.data?.label || node.data?.message || node.data?.question || node.type;
  }, [nodes]);
  const [activeAction, setActiveAction] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addMessage = useCallback((role, content, extra = {}) => {
    setMessages((prev) => [...prev, { role, content, ...extra, ts: Date.now() }]);
  }, []);

  // ─── Gerar fluxo a partir da descricao ───
  const handleGenerate = useCallback(async (description) => {
    if (!description.trim()) return;
    setLoading(true);
    setActiveAction("generate");
    addMessage("user", description);

    try {
      const { data } = await api.post("/flowbuilder/ai/generate", { description });
      if (data.success) {
        addMessage("ai", `Fluxo gerado com ${data.data.nodes?.length || 0} nodes e ${data.data.connections?.length || 0} conexões.`, {
          flowData: data.data,
          tokens: data.tokens,
        });
      } else {
        addMessage("ai", `Erro: ${data.message}`);
      }
    } catch (err) {
      addMessage("ai", `Erro ao gerar: ${err.response?.data?.message || err.message}`);
    }
    setLoading(false);
    setActiveAction(null);
  }, [addMessage]);

  // ─── Modelo pronto (template fixo) ou gerar via IA ───
  const handleQuickPrompt = useCallback((qp) => {
    if (qp.flowData) {
      addMessage("user", qp.label);
      addMessage(
        "ai",
        `Modelo "${qp.label}" pronto: ${qp.flowData.nodes?.length || 0} nodes e ${qp.flowData.connections?.length || 0} conexões.\n\nConfigure a API Key nos nós de IA e, se for Consulta de Produtos, troque a URL DEMO do nó HTTP pela API do seu catálogo antes de salvar.`,
        { flowData: qp.flowData }
      );
      return;
    }
    handleGenerate(qp.text);
  }, [addMessage, handleGenerate]);

  // ─── Explicar fluxo atual ───
  const handleExplain = useCallback(async () => {
    if (!nodes?.length) {
      addMessage("ai", "O fluxo está vazio. Adicione nodes primeiro.");
      return;
    }
    setLoading(true);
    setActiveAction("explain");
    addMessage("user", "Explicar o fluxo atual");

    try {
      const flow = { nodes, connections: edges };
      const { data } = await api.post("/flowbuilder/ai/explain", { flow });
      if (data.success) {
        addMessage("ai", data.data.explanation, { tokens: data.tokens });
      } else {
        addMessage("ai", `Erro: ${data.message}`);
      }
    } catch (err) {
      addMessage("ai", `Erro: ${err.response?.data?.message || err.message}`);
    }
    setLoading(false);
    setActiveAction(null);
  }, [nodes, edges, addMessage]);

  // ─── Detectar problemas ───
  const handleDetectIssues = useCallback(async () => {
    if (!nodes?.length) {
      addMessage("ai", "O fluxo está vazio.");
      return;
    }
    setLoading(true);
    setActiveAction("issues");
    addMessage("user", "Detectar problemas no fluxo");

    try {
      const flow = { nodes, connections: edges };
      const { data } = await api.post("/flowbuilder/ai/detect-issues", { flow });
      if (data.success) {
        const issues = data.data.issues || [];
        if (issues.length === 0) {
          addMessage("ai", "Nenhum problema detectado! O fluxo parece estar correto.", { tokens: data.tokens });
        } else {
          addMessage("ai", `${issues.length} problema(s) encontrado(s):`, {
            issues,
            tokens: data.tokens,
          });
        }
      } else {
        addMessage("ai", `Erro: ${data.message}`);
      }
    } catch (err) {
      addMessage("ai", `Erro: ${err.response?.data?.message || err.message}`);
    }
    setLoading(false);
    setActiveAction(null);
  }, [nodes, edges, addMessage]);

  // ─── Sugerir proximo node ───
  const handleSuggest = useCallback(async () => {
    if (!nodes?.length) {
      addMessage("ai", "O fluxo está vazio. Comece adicionando um node de início.");
      return;
    }
    setLoading(true);
    setActiveAction("suggest");
    addMessage("user", "Sugerir próximos nodes");

    try {
      const flow = { nodes, connections: edges };
      const { data } = await api.post("/flowbuilder/ai/suggest", { flow });
      if (data.success) {
        const suggestions = data.data.suggestions || [];
        if (suggestions.length === 0) {
          addMessage("ai", "Sem sugestões no momento.", { tokens: data.tokens });
        } else {
          const text = suggestions
            .map((s, i) => `${i + 1}. **${s.type}** — ${s.reason}`)
            .join("\n");
          addMessage("ai", `Sugestões:\n${text}`, { tokens: data.tokens });
        }
      } else {
        addMessage("ai", `Erro: ${data.message}`);
      }
    } catch (err) {
      addMessage("ai", `Erro: ${err.response?.data?.message || err.message}`);
    }
    setLoading(false);
    setActiveAction(null);
  }, [nodes, edges, addMessage]);

  // ─── Enviar mensagem personalizada ───
  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    handleGenerate(input.trim());
    setInput("");
  }, [input, handleGenerate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Impede que o ReactFlow capture cliques/eventos neste painel
  const pararPropagacao = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={`${classes.root} ${open ? classes.visible : classes.hidden}`}
      onClick={pararPropagacao}
      onMouseDown={pararPropagacao}
      onPointerDown={pararPropagacao}
    >
      {/* Cabecalho */}
      <div className={classes.header}>
        <SmartToyIcon className={classes.headerIcon} />
        <Typography className={classes.headerTitle}>AI Copilot</Typography>
        <Chip
          size="small"
          label="Beta"
          style={{
            fontSize: 10, height: 20,
            backgroundColor: `${ACCENT}15`, color: ACCENT,
            fontWeight: 700,
          }}
        />
        {messages.length > 0 && (
          <Tooltip title="Nova conversa">
            <IconButton size="small" onClick={() => setMessages([])}>
              <RefreshIcon style={{ fontSize: 18, color: ACCENT }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {/* Conteudo */}
      <div className={classes.content}>
        {/* Acoes rapidas */}
        {messages.length === 0 && (
          <>
            <Typography style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
              Acoes rapidas
            </Typography>
            <div className={classes.quickActions}>
              <Button
                className={classes.actionBtn}
                startIcon={<AutoFixHighIcon style={{ color: ACCENT }} />}
                onClick={() => {}}
                disabled
              >
                Gerar fluxo (descreva abaixo)
              </Button>
              <Button
                className={classes.actionBtn}
                startIcon={<DescriptionIcon style={{ color: "#3b82f6" }} />}
                onClick={handleExplain}
                disabled={loading}
              >
                Explicar fluxo atual
              </Button>
              <Button
                className={classes.actionBtn}
                startIcon={<BugReportIcon style={{ color: "#ef4444" }} />}
                onClick={handleDetectIssues}
                disabled={loading}
              >
                Detectar problemas
              </Button>
              <Button
                className={classes.actionBtn}
                startIcon={<TipsAndUpdatesIcon style={{ color: "#f59e0b" }} />}
                onClick={handleSuggest}
                disabled={loading}
              >
                Sugerir proximo node
              </Button>
            </div>

            <Divider style={{ margin: "8px 0" }} />

            <Typography style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>
              Modelos prontos
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {QUICK_PROMPTS.map((qp) => (
                <Chip
                  key={qp.label}
                  label={qp.label}
                  size="small"
                  onClick={() => handleQuickPrompt(qp)}
                  disabled={loading}
                  style={{
                    fontSize: 11, cursor: "pointer",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />
              ))}
            </div>
          </>
        )}

        {/* Mensagens do chat */}
        <div className={classes.chatArea}>
          {messages.map((msg, idx) => (
            <React.Fragment key={msg.ts + idx}>
              <div
                className={`${classes.messageBubble} ${
                  msg.role === "user" ? classes.userMsg : classes.aiMsg
                }`}
              >
                {msg.content}
                {msg.role === "ai" && msg.content?.includes("API Key OpenAI") && (
                  <Button
                    size="small"
                    onClick={() => window.location.href = "/prompts"}
                    style={{
                      marginTop: 8,
                      textTransform: "none",
                      fontSize: 12,
                      color: ACCENT,
                      fontWeight: 600,
                      padding: "4px 12px",
                      border: `1px solid ${ACCENT}40`,
                      borderRadius: 8,
                      display: "block",
                    }}
                  >
                    Abrir Talk.Ai (configurar API Key)
                  </Button>
                )}
              </div>

              {/* Lista de problemas — clicavel para destacar node no canvas */}
              {msg.issues && msg.issues.map((issue, i) => (
                <div
                  key={i}
                  className={`${classes.issueCard} ${
                    issue.type === "error" ? classes.issueError : classes.issueWarning
                  }`}
                  onClick={() => issue.nodeId && onHighlightNode && onHighlightNode(issue.nodeId)}
                  title={issue.nodeId ? "Clique para localizar no canvas" : ""}
                >
                  <span style={{ fontWeight: 700, flexShrink: 0 }}>
                    {issue.type === "error" ? "❌" : "⚠️"}
                  </span>
                  <div>
                    {issue.message}
                    {issue.nodeId && (
                      <span style={{ fontSize: 10, color: ACCENT, display: "block", marginTop: 2 }}>
                        Clique para localizar
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {/* Botao para aplicar fluxo gerado */}
              {msg.flowData && (
                <Button
                  className={classes.generateBtn}
                  onClick={() => onApplyFlow && onApplyFlow(msg.flowData)}
                  fullWidth
                >
                  Aplicar fluxo gerado ({msg.flowData.nodes?.length} nodes)
                </Button>
              )}

              {/* Uso de tokens */}
              {msg.tokens && (
                <div className={classes.tokenInfo}>
                  Tokens: {msg.tokens.total} ({msg.tokens.prompt}p + {msg.tokens.completion}c)
                </div>
              )}
            </React.Fragment>
          ))}

          {/* Indicador de carregamento */}
          {loading && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 14px", color: "#999",
            }}>
              <CircularProgress size={16} style={{ color: ACCENT }} />
              <span style={{ fontSize: 13 }}>
                {activeAction === "generate" ? "Gerando fluxo..." :
                 activeAction === "explain" ? "Analisando fluxo..." :
                 activeAction === "issues" ? "Verificando problemas..." :
                 activeAction === "suggest" ? "Pensando em sugestoes..." :
                 "Processando..."}
              </span>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Acoes rapidas quando ja tem mensagens no chat */}
        {messages.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Tooltip title="Explicar fluxo">
              <IconButton size="small" onClick={handleExplain} disabled={loading}>
                <DescriptionIcon style={{ fontSize: 18, color: "#3b82f6" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Detectar problemas">
              <IconButton size="small" onClick={handleDetectIssues} disabled={loading}>
                <BugReportIcon style={{ fontSize: 18, color: "#ef4444" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sugerir nodes">
              <IconButton size="small" onClick={handleSuggest} disabled={loading}>
                <TipsAndUpdatesIcon style={{ fontSize: 18, color: "#f59e0b" }} />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Area de entrada de texto */}
      <div className={classes.inputArea}>
        <TextField
          className={classes.textField}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          maxRows={3}
          placeholder="Descreva o fluxo que voce quer..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <IconButton
          size="small"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          style={{ color: input.trim() ? ACCENT : "#ccc" }}
        >
          <SendIcon style={{ fontSize: 20 }} />
        </IconButton>
      </div>
    </div>
  );
}
